import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

export type PreviewRole =
  | "guest"
  | "dancer"
  | "organizer"
  | "studio"
  | "volunteer"
  | "moderator"
  | null;

interface AdminModeContextValue {
  isAdminMode: boolean;
  toggleAdminMode: () => void;
  clearAdminMode: () => void;
  previewRole: PreviewRole;
  setPreviewRole: (role: PreviewRole) => void;
  isPreviewMode: boolean;
  // true when admin controls (edit/moderation buttons) should be visible
  showAdminControls: boolean;
}

const AdminModeContext = createContext<AdminModeContextValue>({
  isAdminMode: false,
  toggleAdminMode: () => {},
  clearAdminMode: () => {},
  previewRole: null,
  setPreviewRole: () => {},
  isPreviewMode: false,
  showAdminControls: false,
});

export function AdminModeProvider({ children }: { children: ReactNode }) {
  const [isAdminMode, setIsAdminMode] = useState(() => {
    try { return localStorage.getItem("admin-mode") === "true"; } catch { return false; }
  });
  const [previewRole, setPreviewRoleState] = useState<PreviewRole>(null);

  const toggleAdminMode = () => {
    setIsAdminMode((prev) => {
      const next = !prev;
      try { localStorage.setItem("admin-mode", String(next)); } catch {}
      if (!next) setPreviewRoleState(null);
      return next;
    });
  };

  const clearAdminMode = () => {
    setIsAdminMode(false);
    setPreviewRoleState(null);
    try { localStorage.removeItem("admin-mode"); } catch {}
  };

  const setPreviewRole = (role: PreviewRole) => {
    setPreviewRoleState(role);
  };

  const isPreviewMode = isAdminMode && previewRole !== null;
  // Admin controls (edit/moderation) show when: admin mode on, and either not previewing
  // or previewing as a role that would see those controls
  const showAdminControls = isAdminMode && (
    !isPreviewMode ||
    previewRole === "moderator" ||
    previewRole === "volunteer"
  );

  return (
    <AdminModeContext.Provider value={{
      isAdminMode,
      toggleAdminMode,
      clearAdminMode,
      previewRole,
      setPreviewRole,
      isPreviewMode,
      showAdminControls,
    }}>
      {children}
    </AdminModeContext.Provider>
  );
}

export function useAdminMode() {
  return useContext(AdminModeContext);
}
